/**
 * AudienceBanner — the room's audience in the side pane, as a small cinematic
 * banner: the audience's cover with a bottom gradient, its name in its own
 * title font (as on the Audiences page cards), a twin count chip, and a link
 * that opens the audience in a new tab.
 *
 * The audience is fetched only when `canFetchAudience` (owners and org
 * collaborators, who can read the org's audience). Shared viewers and guests
 * never fetch. Without data (no fetch, no population, a failed fetch) it shows
 * the plain audience name, with no link and no count.
 */

import { useTranslation } from 'react-i18next'
import useSWR from 'swr'
import { ExternalLink } from 'lucide-react'

import { Text } from '@/components/ui/Text'
import { DisplayFontsLoader } from '@/components/shared/DisplayFontsLoader'
import { populationsApi, AudienceSummary } from '@/lib/api'
import { audienceDisplayName } from '@/lib/audience-display-name'
import { cn } from '@/lib/utils'
import { ROUTES } from '@/routes/routes'
// Cross-page import accepted for v1; moving these helpers to lib/ is a separate cleanup.
import {
  resolveAudienceCanonicalCoverUrl,
  resolveAudienceDigitalTwinCount,
  resolveAudienceTitleFont,
} from '@/pages/AudiencesPage/utils/audience-presentation'
import { getDisplayFontConfig } from '@/pages/AudiencesPage/utils/font-config'

interface AudienceBannerProps {
  /** The population token (`ChatRoomDto.populationId`). */
  populationId: string | null | undefined
  populationName: string | null | undefined
  /** Fetch the audience for the banner; false for shared viewers and guests. */
  canFetchAudience: boolean
  className?: string
}

export function AudienceBanner(props: AudienceBannerProps) {
  const { populationId, populationName, canFetchAudience, className } = props

  const { t, i18n } = useTranslation('app')

  const swrKey = canFetchAudience && populationId ? ['chat-sidebar-audience', populationId] : null
  const { data: audience, isLoading } = useSWR<AudienceSummary>(
    swrKey,
    () => populationsApi.get(populationId!),
    { revalidateOnFocus: false, errorRetryCount: 1 },
  )

  const plainName = audienceDisplayName(populationName)

  if (swrKey && isLoading) {
    return (
      <div
        data-testid="chat-sidebar-audience-banner-loading"
        className={cn('h-28 w-full animate-pulse rounded-xl bg-twin-chat-bubble/40', className)}
      />
    )
  }

  if (!audience || !populationId) {
    if (!plainName) {
      return null
    }
    return (
      <Text
        size="sm"
        data-testid="chat-sidebar-audience-name"
        className={cn('leading-[1.4] text-twin-chat-muted', className)}
      >
        {plainName}
      </Text>
    )
  }

  const name = audienceDisplayName(audience.name) || plainName
  const coverUrl = resolveAudienceCanonicalCoverUrl(audience)
  const titleFont = resolveAudienceTitleFont(audience)
  const fontEntry = getDisplayFontConfig(titleFont).card
  const twinCount = resolveAudienceDigitalTwinCount(audience)

  const cover = coverUrl ? (
    <img
      src={coverUrl}
      alt=""
      loading="lazy"
      data-testid="chat-sidebar-audience-banner-cover"
      className="absolute inset-0 h-full w-full object-cover object-center transition-transform duration-500 ease-out group-hover:scale-105"
    />
  ) : (
    <div
      data-testid="chat-sidebar-audience-banner-gradient"
      className="absolute inset-0 bg-gradient-to-br from-twin-chat-accent/70 via-twin-chat-accent/40 to-twin-chat-bubble"
    />
  )

  const countChip =
    twinCount != null ? (
      <span
        data-testid="chat-sidebar-audience-banner-count"
        className="inline-flex shrink-0 items-center rounded-full bg-black/40 px-2 py-0.5 text-xs font-medium text-white/90 backdrop-blur-sm"
      >
        {t('roomPane.audienceTwinCount', {
          count: twinCount,
          formatted: twinCount.toLocaleString(i18n.language),
        })}
      </span>
    ) : null

  return (
    <a
      href={ROUTES.audience(populationId)}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t('roomPane.openAudience', { name })}
      data-testid="chat-sidebar-audience-banner"
      className={cn(
        'group relative block h-28 w-full shrink-0 overflow-hidden rounded-xl bg-twin-chat-bubble/40',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-twin-chat-accent',
        className,
      )}
    >
      {/* The audience title fonts are not global; the Audiences flow loads them the same way. */}
      <DisplayFontsLoader />
      {cover}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-black/15 to-transparent" />
      <ExternalLink
        aria-hidden="true"
        className="absolute right-2.5 top-2.5 h-3.5 w-3.5 text-white/80 transition-colors group-hover:text-white"
      />
      <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-2 p-3">
        <span
          data-testid="chat-sidebar-audience-banner-name"
          className="line-clamp-2 min-w-0 tracking-tight text-white"
          style={{
            fontFamily: `'${titleFont}', sans-serif`,
            fontSize: `${fontEntry.mobileFontSize}px`,
            lineHeight: fontEntry.lineHeight,
          }}
        >
          {name}
        </span>
        {countChip}
      </div>
    </a>
  )
}
